/**
 * GraveHideSystem — Handles hiding inside coffins.
 *
 * Features:
 * - Player enters open coffin → screen fades dark, "Escondido en el ataúd..."
 * - Player becomes invulnerable while hidden
 * - Press E or Shift to exit the coffin
 * - Coffin closes when player exits
 */
import { BlockId } from './BlockType.js';

export class GraveHideSystem {
  constructor() {
    this.isHiding = false;
    this._gravePos = null; // { x, y, z }
    this._phase = 0;       // 0=not hiding, 1=entering, 2=hidden, 3=exiting
    this._timer = 0;
    this._overlay = null;
    this._text = null;
    this._hintText = null;

    // External references (set by main.js)
    this.world = null;
    this.player = null;
    this.health = null;
    this.hud = null;
    this.interactiveBlocks = null;
    this.tileEntities = null;

    this._keyHandler = null;
    this._buildUI();
  }

  _buildUI() {
    const overlay = document.createElement('div');
    overlay.style.cssText = [
      'position:fixed',
      'top:0',
      'left:0',
      'width:100%',
      'height:100%',
      'background:#000',
      'z-index:955',
      'opacity:0',
      'pointer-events:none',
      'display:flex',
      'flex-direction:column',
      'align-items:center',
      'justify-content:center',
      'gap:16px',
    ].join(';');
    this._overlay = overlay;

    const text = document.createElement('div');
    text.textContent = 'Escondido en el ataúd...';
    text.style.cssText = [
      'color:#fff',
      'font-family:Courier New, monospace',
      'font-size:24px',
      'text-shadow:2px 2px 4px #000',
      'opacity:0',
      'letter-spacing:2px',
    ].join(';');
    overlay.appendChild(text);
    this._text = text;

    const hint = document.createElement('div');
    hint.textContent = 'Presiona E o Shift para salir';
    hint.style.cssText = [
      'color:#aaa',
      'font-family:Courier New, monospace',
      'font-size:14px',
      'text-shadow:1px 1px 2px #000',
      'opacity:0',
    ].join(';');
    overlay.appendChild(hint);
    this._hintText = hint;

    document.body.appendChild(overlay);
  }

  enterGrave(x, y, z) {
    if (this.isHiding) return;

    this.isHiding = true;
    this._gravePos = { x, y, z };
    this._phase = 1; // entering
    this._timer = 0;

    // Save grave coordinates
    if (this.tileEntities) {
      this.tileEntities.saveGrave(x, y, z);
    }

    // Make player invulnerable
    if (this.health) {
      this.health._hiddenInGrave = true;
    }

    // Listen for exit keys
    this._keyHandler = (e) => {
      if (this.isHiding && this._phase === 2) {
        if (e.key === 'e' || e.key === 'E' || e.key === 'Shift') {
          this._exitGrave();
        }
      }
    };
    document.addEventListener('keydown', this._keyHandler);
  }

  _exitGrave() {
    if (!this.isHiding || this._phase !== 2) return;
    this._phase = 3; // exiting
    this._timer = 0;
  }

  update(dt) {
    if (!this.isHiding) return;

    this._timer += dt;

    switch (this._phase) {
      case 1: { // Entering (1s fade to dark)
        const alpha = Math.min(1, this._timer / 1.0);
        this._overlay.style.opacity = String(alpha);
        if (alpha >= 0.95) {
          this._text.style.opacity = '1';
          this._hintText.style.opacity = '1';
        }
        if (this._timer >= 1.0) {
          this._overlay.style.opacity = '1';
          this._text.style.opacity = '1';
          this._hintText.style.opacity = '1';
          this._phase = 2;
          this._timer = 0;
        }
        break;
      }
      case 2: { // Hidden — just wait for player input
        break;
      }
      case 3: { // Exiting (1s fade out)
        const alpha = Math.max(0, 1 - this._timer / 1.0);
        this._overlay.style.opacity = String(alpha);
        if (alpha < 0.5) {
          this._text.style.opacity = '0';
          this._hintText.style.opacity = '0';
        }
        if (this._timer >= 1.0) {
          this._overlay.style.opacity = '0';
          this._text.style.opacity = '0';
          this._hintText.style.opacity = '0';
          this._phase = 0;
          this.isHiding = false;

          // Close coffin
          if (this._gravePos && this.interactiveBlocks) {
            this.interactiveBlocks.toggleCoffin(
              this._gravePos.x, this._gravePos.y, this._gravePos.z
            );
            if (this.world) {
              this.world.updateLoadedChunks(this.player.position);
            }
          }

          // Remove invulnerability
          if (this.health) {
            this.health._hiddenInGrave = false;
          }

          // Cleanup
          if (this._keyHandler) {
            document.removeEventListener('keydown', this._keyHandler);
            this._keyHandler = null;
          }

          if (this.hud) {
            this.hud.showMessage('Has salido del ataúd');
          }

          this._gravePos = null;
        }
        break;
      }
    }
  }
}
